import {
  sortByValueDesc,
  summarizeByPlatform,
  summarizeInvested,
  summarizePortfolio,
  summarizeRealized,
  totalReturn,
  valuateHoldings,
  zipValuations,
  type FxRate,
  type Quote,
  type ValuedHolding,
} from '@porttang/core';
import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useHoldings } from '../holdings/useHoldings';
import { useRealized } from '../realized/useRealized';
import { AllocationChart } from './AllocationChart';
import { HistoryChart } from './HistoryChart';
import { HoldingsCard } from './HoldingsCard';
import { PlatformBreakdown, PlatformTabs } from './PlatformTabs';
import { useFxRates, useQuotes, useRefreshPrices } from '../prices/usePrices';
import {
  formatPercent,
  formatSignedTHB,
  formatTHB,
  formatTime,
  toneClassFor,
} from '../../shared/format';
import { Card, SectionLabel } from '../../shared/pixel';

const EMPTY_QUOTES: ReadonlyMap<string, Quote> = new Map();
const EMPTY_RATES: ReadonlyMap<string, FxRate> = new Map();

/**
 * เวลาที่ราคาชุดนี้ถูกดึงมา — ใช้ตัว **เก่าที่สุด** ไม่ใช่ใหม่ที่สุด
 *
 * ถ้าบอกเวลาของตัวที่สดที่สุด จะทำให้ดูเหมือนทั้งพอร์ตสดกว่าความจริง
 * ตัวเลขที่ผู้ใช้เห็นสดได้เท่ากับข้อมูลที่เก่าที่สุดในนั้นเท่านั้น
 */
function oldestQuoteTime(quotes: ReadonlyMap<string, Quote>, priceKeys: string[]): Date | null {
  let oldest: Date | null = null;
  for (const key of priceKeys) {
    const quote = quotes.get(key);
    if (quote === undefined) {
      continue;
    }
    if (oldest === null || quote.fetchedAt < oldest) {
      oldest = quote.fetchedAt;
    }
  }
  return oldest;
}

/** กล่องเตือน — จุดกะพริบข้างหน้าเพื่อให้สะดุดตาโดยไม่ต้องขยายกล่องให้ใหญ่ */
function Notice({ tone, children }: { tone: 'warn' | 'down'; children: ReactNode }) {
  const palette =
    tone === 'warn'
      ? { box: 'border-warn/25 bg-warn/10 text-warn', dot: 'bg-warn' }
      : { box: 'border-down/25 bg-down/10 text-down', dot: 'bg-down' };

  return (
    <p
      role={tone === 'down' ? 'alert' : undefined}
      className={`mt-4 flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-xs leading-relaxed ${palette.box}`}
    >
      <span aria-hidden className={`mt-1 size-2.5 shrink-0 animate-blink ${palette.dot}`} />
      <span>{children}</span>
    </p>
  );
}

export function DashboardPage() {
  const holdingsQuery = useHoldings();
  const holdings = useMemo(() => holdingsQuery.data ?? [], [holdingsQuery.data]);
  const priceKeys = useMemo(() => holdings.map((holding) => holding.priceKey), [holdings]);

  const quotesQuery = useQuotes(priceKeys);
  const fxQuery = useFxRates();
  const refresh = useRefreshPrices();

  const quotes = quotesQuery.data ?? EMPTY_QUOTES;
  const rates = fxQuery.data ?? EMPTY_RATES;

  const valued = useMemo<ValuedHolding[]>(() => {
    if (holdings.length === 0) {
      return [];
    }
    return zipValuations(holdings, valuateHoldings(holdings, quotes, rates));
  }, [holdings, quotes, rates]);

  /*
    แท็บ "พอร์ตรวม / พอร์ตแยก" — แท็บที่เลือกอยู่ใน URL (`?platform=`)
    รีเฟรชหน้าแล้วยังอยู่แท็บเดิม และกดปุ่มย้อนกลับของเบราว์เซอร์ไม่พาหลุดออกจากแดชบอร์ด

    มีแพลตฟอร์มเดียวไม่ต้องมีแท็บ เพราะพอร์ตแยกก็คือพอร์ตรวม
    ชื่อใน URL ที่ไม่มีอยู่แล้ว (ลบ holding ตัวสุดท้ายไป หรือลิงก์เก่า) กลับไปพอร์ตรวม ไม่ใช่หน้าว่าง
  */
  const [searchParams, setSearchParams] = useSearchParams();
  const platforms = useMemo(() => summarizeByPlatform(valued), [valued]);
  const showTabs = platforms.length > 1;
  const requested = searchParams.get('platform');
  const platform =
    showTabs && platforms.some((item) => item.platform === requested) ? requested : null;

  const scoped = useMemo(
    () =>
      platform === null ? valued : valued.filter(({ holding }) => holding.platform === platform),
    [valued, platform],
  );
  const scopedPriceKeys = useMemo(() => scoped.map(({ holding }) => holding.priceKey), [scoped]);

  // มูลค่ารวมนับเงินสด แต่ต้นทุน กำไร และ % ไม่นับ — เงินสดเจือจาง % โดยไม่ได้บอกผลงานการลงทุน (spec 5.2)
  const totals = useMemo(() => summarizePortfolio(scoped), [scoped]);
  const invested = useMemo(() => summarizeInvested(scoped), [scoped]);
  const hasCash = scoped.some(({ holding }) => holding.assetType === 'cash');
  const rows = useMemo(() => sortByValueDesc(scoped), [scoped]);
  const asOf = useMemo(() => oldestQuoteTime(quotes, scopedPriceKeys), [quotes, scopedPriceKeys]);

  // ผลตอบแทนที่รับรู้แล้ว — โหลดไม่สำเร็จก็แค่ไม่แสดงส่วนนี้ ห้ามทำให้ทั้งหน้าพัง
  const realizedQuery = useRealized();
  // รายการขาย/ปันผลคัดลอกชื่อแพลตฟอร์มมาเก็บเอง จึงแยกพอร์ตได้แม้ holding ต้นทางถูกลบไปแล้ว
  const realizedEntries = useMemo(
    () =>
      realizedQuery.data === undefined || platform === null
        ? realizedQuery.data
        : realizedQuery.data.filter((entry) => entry.platform === platform),
    [realizedQuery.data, platform],
  );
  const realizedTotals = useMemo(
    () => (realizedEntries === undefined ? null : summarizeRealized(realizedEntries, rates)),
    [realizedEntries, rates],
  );
  const overall = useMemo(
    () => (realizedTotals === null ? null : totalReturn(totals, realizedTotals)),
    [totals, realizedTotals],
  );
  const hasRealized = (realizedEntries?.length ?? 0) > 0;

  const isLoadingPrices = quotesQuery.isPending || fxQuery.isPending;
  const isUp = Number(invested.pnlTHB ?? 0) >= 0;

  // replace ไม่ใช่ push — สลับแท็บไปมาไม่ควรกองประวัติจนต้องกดย้อนกลับหลายรอบกว่าจะออกจากหน้า
  function selectPlatform(next: string | null) {
    setSearchParams(next === null ? {} : { platform: next }, { replace: true });
  }

  if (holdingsQuery.isPending) {
    return <p className="py-16 text-center font-mono text-sm text-ink-500">กำลังโหลด…</p>;
  }

  if (holdings.length === 0) {
    return (
      <section className="rounded-[14px] border border-dashed border-line-dash px-6 py-16 text-center">
        <SectionLabel>NO DATA YET</SectionLabel>
        <h1 className="mt-4 text-xl font-semibold text-ink-100">ยังไม่มีอะไรให้สรุป</h1>
        <p className="mt-2 text-sm text-ink-400">
          เพิ่มสินทรัพย์ที่ถืออยู่ก่อน แล้วหน้านี้จะคำนวณมูลค่าและกำไรขาดทุนให้
        </p>
        <Link
          to="/holdings"
          className="mt-6 inline-block rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink-100 transition hover:brightness-110"
        >
          ไปหน้าสินทรัพย์
        </Link>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {showTabs && (
        <PlatformTabs platforms={platforms} selected={platform} onSelect={selectPlatform} />
      )}

      {/* ยอดรวมกับกราฟย้อนหลังอยู่การ์ดเดียวกัน เพราะเป็นตัวเลขก้อนเดียวกันคนละมุมมอง */}
      <Card className="animate-rise bg-linear-to-b from-raised to-raised-deep p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <SectionLabel>
              {platform === null ? 'TOTAL NET WORTH' : 'PLATFORM NET WORTH'}
            </SectionLabel>
            {platform !== null && (
              <h1 className="mt-2 truncate text-lg font-semibold text-ink-100">{platform}</h1>
            )}

            <div className="mt-3 flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-[34px] leading-none font-bold tracking-[-1.5px] text-ink-100 sm:text-[44px]">
                {formatTHB(totals.valueTHB)}
              </span>
              {/* ไม่มี % ก็ไม่ต้องมีป้าย — ป้ายเขียวที่เขียนว่า — สื่อว่ากำไร ทั้งที่ยังไม่รู้อะไรเลย */}
              {invested.pnlPct !== null && (
                <span className="flex items-baseline gap-2">
                  <span
                    className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 font-mono text-sm font-bold ${
                      isUp ? 'bg-up/12 text-up' : 'bg-down/12 text-down'
                    }`}
                  >
                    <span aria-hidden className={`size-2 ${isUp ? 'bg-up' : 'bg-down'}`} />
                    {formatPercent(invested.pnlPct)}
                  </span>
                  {hasCash && <span className="text-xs text-ink-400">(ไม่รวมเงินสด)</span>}
                </span>
              )}
            </div>

            <p className="mt-2.5 text-sm text-ink-400">
              กำไร/ขาดทุน{' '}
              <span className={`font-mono ${toneClassFor(invested.pnlTHB)}`}>
                {formatSignedTHB(invested.pnlTHB)}
              </span>{' '}
              · รวมจาก {scoped.length} รายการ ·{' '}
              {asOf === null ? 'ยังไม่เคยดึงราคา' : `ราคา ณ ${formatTime(asOf)}`}
            </p>
          </div>

          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="rounded-lg border border-line-strong bg-sunken px-3.5 py-2.5 text-[13px] text-ink-300 transition hover:border-brand hover:text-brand-text disabled:opacity-60"
          >
            <span className="flex items-center gap-2">
              {/* วาดไอคอนเอง — อักขระ ↻ ไม่มีในฟอนต์ที่แอปโหลด เบราว์เซอร์จะไปหยิบตัวอื่นมาแทนจนอ่านไม่ออก */}
              <svg viewBox="0 0 16 16" aria-hidden className="size-3.5" fill="currentColor">
                <path d="M8 2.5a5.5 5.5 0 1 0 5.24 3.82l-1.43-.46A4 4 0 1 1 8 4v2.2l3.2-2.85L8 .5v2Z" />
              </svg>
              {refresh.isPending ? 'กำลังดึงราคา…' : 'รีเฟรชราคา'}
            </span>
          </button>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-400">ต้นทุนรวม{hasCash && ' (ไม่รวมเงินสด)'}</dt>
            <dd className="mt-1 font-mono text-[19px] text-ink-100">
              {formatTHB(invested.costTHB)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-400">กำไร / ขาดทุน</dt>
            <dd className={`mt-1 font-mono text-[19px] ${toneClassFor(invested.pnlTHB)}`}>
              {formatSignedTHB(invested.pnlTHB)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-400">เปลี่ยนแปลง</dt>
            <dd className={`mt-1 font-mono text-[19px] ${toneClassFor(invested.pnlTHB)}`}>
              {formatPercent(invested.pnlPct)}
            </dd>
          </div>
        </dl>

        {/*
          ตอบคำถาม "ตั้งแต่เริ่มลงทุนมา กำไรจริงไหม" — กำไรของที่ถืออยู่ข้างบนไม่นับของที่ขายไปแล้ว
          แสดงเฉพาะเมื่อมีรายการ ไม่งั้นจะเป็นแถวศูนย์ที่ซ้ำกับตัวเลขข้างบนทุกช่อง
        */}
        {hasRealized && realizedTotals !== null && overall !== null && (
          <div className="mt-4 border-t border-line pt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs text-ink-400">รวมที่ขายไปแล้วและปันผล</span>
              <Link
                to="/realized"
                className="text-[13px] text-ink-400 transition hover:text-brand-text"
              >
                ดูรายการ →
              </Link>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-ink-400">กำไรจากการขาย</dt>
                <dd
                  className={`mt-1 font-mono text-[17px] ${toneClassFor(realizedTotals.salePnlTHB)}`}
                >
                  {formatSignedTHB(realizedTotals.salePnlTHB)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-ink-400">ปันผลสะสม</dt>
                <dd
                  className={`mt-1 font-mono text-[17px] ${toneClassFor(realizedTotals.dividendsTHB)}`}
                >
                  {formatSignedTHB(realizedTotals.dividendsTHB)}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <dt className="text-xs text-ink-400">ผลตอบแทนรวมทั้งหมด</dt>
                <dd
                  className={`mt-1 font-mono text-[17px] font-semibold ${toneClassFor(overall.totalReturnTHB)}`}
                >
                  {formatSignedTHB(overall.totalReturnTHB)}
                </dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] text-ink-600">
              ผลตอบแทนรวม = กำไร/ขาดทุนของที่ถืออยู่ + กำไรจากการขาย + ปันผล
              {overall.isPartial && !isLoadingPrices && ' · ⚠ ยังนับไม่ครบทุกรายการ'}
            </p>
          </div>
        )}

        {realizedQuery.isError && (
          <Notice tone="warn">
            โหลดรายการขายและปันผลไม่สำเร็จ — ตัวเลขข้างบนยังถูกต้อง แต่ไม่รวมกำไรที่ขายไปแล้ว
          </Notice>
        )}

        {totals.isPartial && (
          <Notice tone="warn">
            ยอดรวมยังไม่ครบ — มี {totals.incompleteCount} รายการที่ยังตีมูลค่าไม่ได้
            ตัวเลขข้างบนจึงไม่ได้นับรวมรายการเหล่านั้นทั้งต้นทุนและมูลค่า
          </Notice>
        )}

        {refresh.isError && <Notice tone="down">ดึงราคาไม่สำเร็จ: {refresh.error.message}</Notice>}

        {refresh.isSuccess && refresh.data.failed.length > 0 && (
          <Notice tone="warn">
            ดึงราคาไม่สำเร็จ {refresh.data.failed.length} รายการ:{' '}
            {refresh.data.failed.map((item) => `${item.priceKey} (${item.reason})`).join(', ')}
          </Notice>
        )}

        <div className="mt-6 border-t border-line pt-5">
          <HistoryChart platform={platform} />
        </div>
      </Card>

      {/* พอร์ตรวม: แยกให้เห็นทีละแพลตฟอร์ม กดแถวแล้วเข้าแท็บของแพลตฟอร์มนั้น */}
      {showTabs && platform === null && (
        <PlatformBreakdown
          platforms={platforms}
          onSelect={(next) => {
            selectPlatform(next);
            // แถวนี้อยู่กลางหน้า — เลื่อนกลับขึ้นไปให้เห็นยอดของแพลตฟอร์มที่เพิ่งเลือก
            globalThis.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}

      {/* สัดส่วนพอร์ต — วงแหวนกับรายละเอียดแยกกันคนละการ์ดอยู่ข้างใน */}
      <AllocationChart items={rows} hidePlatform={platform !== null} />

      {/* พอร์ตรวมที่มีหลายแพลตฟอร์ม: แบ่ง section ตามแพลตฟอร์ม พับ/กางได้ */}
      <HoldingsCard
        rows={rows}
        quotes={quotes}
        isLoadingPrices={isLoadingPrices}
        platforms={showTabs && platform === null ? platforms : null}
        showPlatformName={!showTabs}
      />
    </div>
  );
}
